import { useEffect, useRef } from 'react'
import { Icon } from './Icon'

interface Props {
  title: string
  description: string
  confirmLabel: string
  cancelLabel?: string
  tone?: 'danger' | 'warning'
  onCancel: () => void
  onConfirm: () => void
}

export function ConfirmDialog({ title, description, confirmLabel, cancelLabel = 'Cancelar', tone = 'danger', onCancel, onConfirm }: Props) {
  const dialog = useRef<HTMLElement>(null)
  const cancelButton = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    cancelButton.current?.focus()

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        onCancel()
        return
      }
      if (event.key !== 'Tab') return
      const buttons = [...(dialog.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? [])]
      if (!buttons.length) return
      const first = buttons[0]
      const last = buttons[buttons.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown, true)
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)
      document.body.style.overflow = previousOverflow
      previousFocus?.focus()
    }
  }, [onCancel])

  return <div className="confirm-backdrop" data-confirm-dialog onMouseDown={event => event.target === event.currentTarget && onCancel()}>
    <section ref={dialog} className="confirm-dialog" role="alertdialog" aria-modal="true" aria-labelledby="confirm-dialog-title" aria-describedby="confirm-dialog-description">
      <div className={`confirm-dialog-icon ${tone}`}><Icon name={tone === 'warning' ? 'warning' : 'trash'}/></div>
      <div className="confirm-dialog-copy">
        <span>Confirmação necessária</span>
        <h2 id="confirm-dialog-title">{title}</h2>
        <p id="confirm-dialog-description">{description}</p>
      </div>
      <div className="confirm-dialog-actions">
        <button ref={cancelButton} type="button" className="secondary" onClick={onCancel}>{cancelLabel}</button>
        <button type="button" className={tone === 'warning' ? 'primary' : 'danger-action'} onClick={onConfirm}><Icon name={tone === 'warning' ? 'check' : 'trash'}/>{confirmLabel}</button>
      </div>
    </section>
  </div>
}
